import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import DonutChart from '../../components/Chart/DonutChart';
import HorizontalBarChart from '../../components/Chart/HorizontalBarChart';
import LineChartCard from '../../components/Chart/LineChartCard';
import DashboardStatCard from '../../components/Dashboard/DashboardStatCard';
import DashboardPanel from '../../components/Layout/DashboardPanel';
import DateRangePicker from '../../components/DateRangePicker/DateRangePicker';
import Table from '../../components/Table/Table/Table';
import { useProductionStatAPI } from '../../api/production/useProductionStatAPI';
import ProductionDailyInputModal from './ProductionDailyInputModal';
import './ProductionStatDashboard.css';

/**
 * 트랙 R-Production-Merge — `/production` 페이지 *내부* 통계 위젯.
 *
 * R3b 의 ProductionStat 페이지 본체를 *PageShell 제거* 한 형태로 추출
 * (수주관리 ContractStatDashboard 패턴). ProductionManagement 페이지의
 * 최상단에 `<ProductionStatDashboard />` 1줄로 부착된다.
 *
 * 2026-05-22: 날짜 컨트롤을 검색 버튼 방식 → DateRangePicker(기간 변경 시 자동 조회)로
 *   교체하여 /contract(ContractStatDashboard) 패턴과 통일 (검색 버튼 제거).
 *
 * 가동률 은 외부 PLC 실측 지표라 mes 가 못 만들고 외부 설비값에서 옴 — read-only.
 * null 이면 "—". frontend 재산출 절대 금지 (KPI_MASTER_SPEC §11).
 */
const round2 = (v) => Math.round(v * 100) / 100;

const diffDaysInclusive = (startDt, endDt) => {
  if (!startDt || !endDt) return 1;
  const start = new Date(startDt);
  const end = new Date(endDt);
  return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
};

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const computeDefaultRange = (days) => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - days);
  return { startDt: isoDate(start), endDt: isoDate(today) };
};

const ProductionStatDashboard = () => {
  const session = useSelector((state) => state.session?.data) || {};
  const companySeq = session.companySeq;
  const api = useProductionStatAPI();

  const [period, setPeriod] = useState(() => computeDefaultRange(30));
  const [summary, setSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // 생산 실적 입력/수정 모달 상태 ('create' = 생산 등록, 'edit' = 로그 행 drill-down)
  const [modal, setModal] = useState({ open: false, mode: 'create', occurDt: null });

  // 회사/기간 변경(또는 저장 후 refreshKey 증가) 시 자동 조회 (검색 버튼 없음 — ContractStatDashboard 패턴).
  useEffect(() => {
    if (!companySeq) return;
    if (!period.startDt || !period.endDt) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.summary({ companySeq, startDt: period.startDt, endDt: period.endDt });
        if (!cancelled) setSummary(res || null);
      } catch (err) {
        if (!cancelled) setSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySeq, period.startDt, period.endDt, refreshKey]);

  const openCreateModal = () => setModal({ open: true, mode: 'create', occurDt: null });
  const openEditModal = (occurDt) =>
    setModal({ open: true, mode: 'edit', occurDt });
  const closeModal = () => setModal((m) => ({ ...m, open: false }));
  // 저장/수정 성공 시 통계·차트·로그 즉시 재조회
  const handleSaved = () => setRefreshKey((k) => k + 1);

  // 일평균 = 총량 / 기간일수 (단순 산식)
  const dailyAvg = useMemo(() => {
    if (!summary || !summary.totalProduction) return 0;
    const days = diffDaysInclusive(period.startDt, period.endDt);
    return Math.round(summary.totalProduction / days);
  }, [summary, period]);

  const donutData = useMemo(() => {
    const list = summary?.topProducts || [];
    return list.map((p) => ({
      label: p.productName ?? '미상',
      value: Number(p.amount) || 0,
    }));
  }, [summary]);

  const tableColumns = [
    { header: '일자', accessor: 'occurDt' },
    {
      header: '생산',
      accessor: 'production',
      render: (row) => (Number(row.production) || 0).toLocaleString(),
    },
    {
      header: '불량',
      accessor: 'defect',
      render: (row) => (Number(row.defect) || 0).toLocaleString(),
    },
  ];

  // 일자별 생산 로그 — 최신(occurDt 큰 값) 이 상단에 보이도록 desc 정렬
  const tableData = (summary?.daily || [])
    .slice()
    .sort((a, b) => String(b.occurDt || '').localeCompare(String(a.occurDt || '')))
    .map((d, i) => ({
      id: `${d.occurDt}-${i}`,
      occurDt: d.occurDt,
      production: d.production,
      defect: d.defect,
    }));

  const formatPercent = (v) => (typeof v === 'number' ? round2(v) : '—');

  const uptimeIsNull =
    summary?.uptimeRate === null || summary?.uptimeRate === undefined;

  const defectRateIsNull =
    summary?.defectRate === null || summary?.defectRate === undefined;

  return (
    <DashboardPanel
      controls={
        <DateRangePicker
          startKey="startDt"
          endKey="endDt"
          formData={period}
          setFormData={setPeriod}
          split
        />
      }
    >
      <div className="production-stat-cards">
        <DashboardStatCard
          title="기간 총 생산량"
          value={summary?.totalProduction ?? 0}
          unit="개"
        />
        <DashboardStatCard
          title="일 평균 생산량"
          value={dailyAvg}
          unit="개/일"
        />
        <DashboardStatCard
          title="불량률"
          value={defectRateIsNull ? '—' : formatPercent(summary.defectRate)}
          unit={defectRateIsNull ? '' : '%'}
        />
        <DashboardStatCard
          title="가동률"
          value={uptimeIsNull ? '—' : round2(summary.uptimeRate)}
          unit={uptimeIsNull ? '' : '%'}
        />
      </div>

      <div className="production-stat-charts">
        <div className="production-stat-chart-card">
          <DonutChart
            data={donutData}
            title="제품별 생산 비중 (탑5+기타)"
            width={400}
            height={300}
          />
        </div>
        <div className="production-stat-chart-card">
          <HorizontalBarChart
            data={donutData}
            title="제품별 수량 (탑5)"
            width={400}
            height={300}
          />
        </div>
        <div className="production-stat-chart-card production-stat-chart-wide">
          <LineChartCard
            data={summary?.daily || []}
            xKey="occurDt"
            yKey="production"
            yKey2="defect"
            yLabel="생산"
            yLabel2="불량"
            title="일별 생산 추이"
            width="100%"
            height={284}
          />
        </div>
      </div>

      <div className="production-stat-table">
        <div className="production-stat-table__toolbar">
          <button
            type="button"
            className="production-stat-register-btn"
            onClick={openCreateModal}
          >
            생산 등록
          </button>
        </div>
        <Table
          title="일자별 생산 로그"
          columns={tableColumns}
          data={tableData}
          getRowId={(row) => row.id}
          showSearchBar={false}
          onRowClick={(row) => openEditModal(row.occurDt)}
        />
      </div>

      <ProductionDailyInputModal
        isOpen={modal.open}
        mode={modal.mode}
        companySeq={companySeq}
        occurDt={modal.occurDt}
        onClose={closeModal}
        onSuccess={handleSaved}
      />
    </DashboardPanel>
  );
};

export default ProductionStatDashboard;
